import * as ko from 'knockout';
import {global, lm} from '../../appContext';
import {createErrorMessage, escapeHTML, getSearchMarkup} from '../../util/misc';
import inline_renameHtml from './inline-rename.html?raw';

export const template = inline_renameHtml;

export interface IInlineRenameParams {
    /** Mutable list-row object; `name` is updated in place on success. */
    item: {id: number; name: string}
    canEdit: () => boolean
    /** Translation key for the native locked tooltip. */
    lockedTitleKey: string
    save: (newName: string) => Promise<void>
    searchKey?: KnockoutObservable<string> | (() => string)
    maxLength?: number
}

export class viewModel {
    readonly draft: KnockoutObservable<string>;
    readonly displayName: KnockoutObservable<string>;
    readonly isEditing = ko.observable(false);
    readonly isSaving = ko.observable(false);
    readonly showSaved = ko.observable(false);
    readonly inputHasFocus = ko.observable(false);

    readonly canEdit: () => boolean;
    readonly maxLength: number | undefined;

    private readonly item: IInlineRenameParams['item'];
    private readonly lockedTitleKey: string;
    private readonly saveFn: (newName: string) => Promise<void>;
    private readonly searchKey: KnockoutObservable<string> | (() => string) | undefined;

    private dismissReason: 'none' | 'cancel' | 'commit' = 'none';
    private savedHideTimer: number | null = null;
    private originalName = '';

    constructor(params: IInlineRenameParams) {
        this.item = params.item;
        this.canEdit = params.canEdit;
        this.lockedTitleKey = params.lockedTitleKey;
        this.saveFn = params.save;
        this.searchKey = params.searchKey;
        this.maxLength = params.maxLength;

        this.displayName = ko.observable(params.item.name ?? '');
        this.draft = ko.observable(params.item.name ?? '');
    }

    readonly rootCss = ko.pureComputed(() => ({
        'inline-rename--editable': this.canEdit(),
        'inline-rename--locked': !this.canEdit(),
        'inline-rename--editing': this.isEditing(),
        'inline-rename--saving': this.isSaving(),
    }));

    readonly displayHtml = ko.pureComputed(() => {
        // Escape before html binding; getSearchMarkup only wraps matches in <mark>.
        const name = escapeHTML(this.displayName());
        const key = this.resolveSearchKey();
        if (key) {
            return getSearchMarkup(name, key);
        }
        return name;
    });

    readonly displayTitle = ko.pureComputed(() => {
        if (!this.canEdit()) {
            return lm.t(this.lockedTitleKey) ?? '';
        }
        return '';
    });

    onEditClick = (): void => {
        if (!this.canEdit() || this.isSaving()) {
            return;
        }
        this.startEdit();
    };

    onInputFocus = (_data: unknown, event: FocusEvent): void => {
        const el = event.target as HTMLInputElement | null;
        if (!el) {
            return;
        }
        el.select();
        // Keep the start of the value visible after select() (avoids scrolling long text right).
        el.scrollLeft = 0;
    };

    /** Cancel on mousedown so blur never auto-saves when clearing. */
    onClearMouseDown = (_data: unknown, event: MouseEvent): boolean => {
        event.preventDefault();
        this.cancel();
        return false;
    };

    onCancelClick = (): void => {
        if (this.isEditing()) {
            this.cancel();
        }
    };

    onKeyDown = (_data: unknown, event: KeyboardEvent): boolean => {
        if (event.key === 'Enter') {
            event.preventDefault();
            void this.commit();
            return false;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            this.cancel();
            return false;
        }
        return true;
    };

    onBlur = (): void => {
        window.setTimeout(() => {
            if (!this.isEditing() || this.dismissReason !== 'none' || this.isSaving()) {
                return;
            }
            void this.commit();
        }, 0);
    };

    private startEdit(): void {
        this.clearSavedBadge();
        this.dismissReason = 'none';
        this.originalName = this.displayName();
        this.draft(this.originalName);
        this.isEditing(true);
        this.inputHasFocus(true);
    }

    private cancel(): void {
        this.dismissReason = 'cancel';
        this.draft(this.originalName);
        this.isEditing(false);
        this.inputHasFocus(false);
    }

    private async commit(): Promise<void> {
        if (this.dismissReason === 'commit' || this.isSaving()) {
            return;
        }

        const next = this.draft().trim();
        // Empty or unchanged — exit without an API call.
        if (!next || next === this.originalName) {
            this.cancel();
            return;
        }

        if (/[<>]/gm.test(next)) {
            global.snackbarData({message: '((contains invalid characters))', type: 'danger'});
            this.inputHasFocus(true);
            return;
        }

        this.dismissReason = 'commit';
        this.isSaving(true);
        try {
            await this.saveFn(next);
            this.item.name = next;
            this.displayName(next);
            this.isEditing(false);
            this.inputHasFocus(false);
            this.flashSavedBadge();
        } catch (reason) {
            this.dismissReason = 'none';
            global.snackbarData(createErrorMessage(String(reason)));
            this.inputHasFocus(true);
        } finally {
            this.isSaving(false);
        }
    }

    private resolveSearchKey(): string {
        const key = this.searchKey;
        if (!key) {
            return '';
        }
        if (ko.isObservable(key)) {
            return String(key() ?? '');
        }
        return String(key() ?? '');
    }

    private flashSavedBadge(): void {
        this.clearSavedBadge();
        this.showSaved(true);
        this.savedHideTimer = window.setTimeout(() => {
            this.showSaved(false);
            this.savedHideTimer = null;
        }, 2000);
    }

    private clearSavedBadge(): void {
        if (this.savedHideTimer != null) {
            window.clearTimeout(this.savedHideTimer);
            this.savedHideTimer = null;
        }
        this.showSaved(false);
    }

    dispose(): void {
        this.clearSavedBadge();
    }
}
